import {
	organicStatusLabel,
	PlantingDTO,
	plantMethodLabel,
} from "seedgreen-shared/dtos/PlantingDTO";
import { computed, ComputedRef, Ref } from "vue";
import { Position } from "geojson";
import { useI18n } from "vue-i18n";
import { formatDateString } from "@/utils/date";

// distanceTo() returns kilometers (turf default); we display imperial,
// consistent with the map scale control in useMapboxControls.ts
const KM_TO_MILES = 0.621371;

/**
 * Get a method for display a field based on a given Planting object
 */
function createPlantingDisplayFields(t: (key: string) => string) {
	return {
		distance: {
			label: t("lot-search.distance"),
			fn: (
				planting: PlantingDTO,
				userPosition: Position | undefined,
				t: (key: string) => string,
			) => {
				if (!userPosition) return "";
				const dist = planting.distanceTo(userPosition);
				if (dist === undefined) return t("common.unknown");
				return (
					(dist * KM_TO_MILES).toFixed(1) +
					" " +
					t("measure.mile-abv")
				);
			},
		},
		"wet-date": {
			fn: (planting: PlantingDTO) =>
				formatDateString(planting.wetDateCurrent),
			label: t("planting-details.wet-date"),
		},
		variety: {
			fn: (planting: PlantingDTO) => planting.varietyName ?? "",
			label: t("planting.variety"),
		},
		"harvest-dates": {
			fn: (planting: PlantingDTO) =>
				planting.harvestDates
					.map((d) => formatDateString(d))
					.join(", "),
			label: t("planting-details.harvest-dates"),
		},
		"external-id": {
			fn: (planting: PlantingDTO) => planting.externalId ?? "",
			label: t("planting-details.id"),
		},
		acres: {
			fn: (planting: PlantingDTO) => planting.acresCurrent.toFixed(1),
			label: t("planting-details.acres"),
		},
		"organic-status": {
			fn: (planting: PlantingDTO) =>
				organicStatusLabel(planting.organicStatus),
			label: t("harvest-overview.organic-status"),
		},
		grower: {
			fn: (planting: PlantingDTO) => planting.growerName ?? "",
			label: t("planting-details.grower"),
		},
		"plant-method": {
			fn: (planting: PlantingDTO) =>
				plantMethodLabel(planting.plantMethod),
			label: t("planting.plant-method"),
		},
	} satisfies Record<string, PlantingDisplayField>;
}

export function usePlantingDisplayField(
	key: Ref<PlantingDisplayFieldKey>,
): ComputedRef<PlantingDisplayField> {
	const { t } = useI18n();
	const translatedFields = computed(() => createPlantingDisplayFields(t));
	return computed(() => translatedFields.value[key.value]);
}

export function usePlantingDisplayFields(): {
	PlantingDisplayFields: Record<
		PlantingDisplayFieldKey,
		PlantingDisplayField
	>;
} {
	const { t } = useI18n();
	const PlantingDisplayFields = createPlantingDisplayFields(t);
	return { PlantingDisplayFields };
}

export interface PlantingDisplayField {
	fn: (
		planting: PlantingDTO,
		userPosition: Position | undefined,
		t: (key: string) => string,
	) => string;
	label: string;
}

export type PlantingDisplayFieldKey =
	| "distance"
	| "wet-date"
	| "variety"
	| "harvest-dates"
	| "external-id"
	| "acres"
	| "organic-status"
	| "grower"
	| "plant-method";
